import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  type DocumentSnapshot,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { firestore } from '../../firebase';
import {
  EMPTY_REVIEW_STATS,
  normalizeReview,
  normalizeStats,
  type PartnerReview,
  type PartnerReviewStats,
  reviewIdFor,
  type StarValue,
} from './reviewsApi';

export const REVIEWS_PAGE_SIZE = 10;

// Live aggregate for the profile summary and the Reviews tab header.
export function usePartnerReviewStats(venueId: string | null) {
  const [state, setState] = useState<{ venueId: string | null; stats: PartnerReviewStats; loaded: boolean }>({ venueId, stats: EMPTY_REVIEW_STATS, loaded: false });
  useEffect(() => {
    if (!venueId) return undefined;
    return onSnapshot(
      doc(firestore, 'partnerReviewStats', venueId),
      snap => setState({ venueId, stats: normalizeStats(snap.exists() ? snap.data() : null), loaded: true }),
      error => {
        console.warn('[Tonyte][PartnerReviews] stats listener failed', { venueId, error });
        setState({ venueId, stats: EMPTY_REVIEW_STATS, loaded: true });
      },
    );
  }, [venueId]);
  const current = state.venueId === venueId;
  return { stats: current ? state.stats : EMPTY_REVIEW_STATS, loaded: current && state.loaded };
}

// The signed-in user's own review of this partner (any status), live.
export function useMyPartnerReview(venueId: string | null, uid: string | null) {
  const [state, setState] = useState<{ key: string; review: PartnerReview | null; loaded: boolean }>({ key: '', review: null, loaded: false });
  const key = venueId && uid ? reviewIdFor(venueId, uid) : '';
  useEffect(() => {
    if (!key) return undefined;
    return onSnapshot(
      doc(firestore, 'partnerReviews', key),
      snap => setState({ key, review: snap.exists() ? normalizeReview(snap.id, snap.data()) : null, loaded: true }),
      error => {
        console.warn('[Tonyte][PartnerReviews] own review listener failed', { key, error });
        setState({ key, review: null, loaded: true });
      },
    );
  }, [key]);
  if (!key) return { review: null, loaded: true };
  return state.key === key ? { review: state.review, loaded: state.loaded } : { review: null, loaded: false };
}

// Published reviews, newest first, optionally only one star rating; loaded a
// page at a time with a Firestore cursor.
export function usePartnerReviewList(venueId: string | null, star: StarValue | null, enabled = true) {
  const [reviews, setReviews] = useState<PartnerReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);
  const cursorRef = useRef<DocumentSnapshot | null>(null);
  const requestRef = useRef(0);

  const buildQuery = useCallback((after: DocumentSnapshot | null) => {
    const constraints = [
      where('venueId', '==', venueId),
      where('status', '==', 'published'),
      ...(star ? [where('rating', '==', star)] : []),
      orderBy('createdAt', 'desc'),
      ...(after ? [startAfter(after)] : []),
      limit(REVIEWS_PAGE_SIZE),
    ];
    return query(collection(firestore, 'partnerReviews'), ...constraints);
  }, [star, venueId]);

  const reload = useCallback(async () => {
    if (!venueId) return;
    const request = (requestRef.current += 1);
    setLoading(true);
    setError(false);
    try {
      const snap = await getDocs(buildQuery(null));
      if (request !== requestRef.current) return;
      cursorRef.current = snap.docs[snap.docs.length - 1] ?? null;
      setReviews(snap.docs.map(item => normalizeReview(item.id, item.data())));
      setHasMore(snap.docs.length === REVIEWS_PAGE_SIZE);
    } catch (err) {
      if (request !== requestRef.current) return;
      console.warn('[Tonyte][PartnerReviews] list failed', { venueId, star, err });
      setError(true);
      setReviews([]);
      setHasMore(false);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [buildQuery, star, venueId]);

  const loadMore = useCallback(async () => {
    if (!venueId || loading || loadingMore || !hasMore || !cursorRef.current) return;
    const request = requestRef.current;
    setLoadingMore(true);
    try {
      const snap = await getDocs(buildQuery(cursorRef.current));
      if (request !== requestRef.current) return;
      cursorRef.current = snap.docs[snap.docs.length - 1] ?? cursorRef.current;
      setReviews(current => {
        const seen = new Set(current.map(item => item.id));
        return [...current, ...snap.docs.filter(item => !seen.has(item.id)).map(item => normalizeReview(item.id, item.data()))];
      });
      setHasMore(snap.docs.length === REVIEWS_PAGE_SIZE);
    } catch (err) {
      console.warn('[Tonyte][PartnerReviews] next page failed', { venueId, star, err });
    } finally {
      if (request === requestRef.current) setLoadingMore(false);
    }
  }, [buildQuery, hasMore, loading, loadingMore, star, venueId]);

  useEffect(() => {
    if (!enabled) return;
    reload();
  }, [enabled, reload]);

  return { reviews, loading, loadingMore, hasMore, error, reload, loadMore };
}
